import classNames from 'classnames'
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import styles from './Drawer.module.scss'
import type { DragInfoType } from './types'

interface DragHandlerProps {
  onDragStart?: (arg0: object | null) => void
  onDrag?: (arg0: object | null) => void
  onDragEnd?: (arg0: object | null) => void
}

const DragHandler = ({ onDragStart, onDrag, onDragEnd }: DragHandlerProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const [dragInfo, setDragInfo] = useState<DragInfoType | null>({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
  })
  const handleDragStart = useCallback(
    (event: MouseEvent) => {
      setIsDragging(true)
      setDragInfo(() => ({ startX: event.clientX, startY: event.clientY }))
      if (onDragStart) {
        onDragStart(dragInfo)
      }
    },
    [dragInfo, onDragStart]
  )
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isDragging) return
      const newDragInfo = { ...dragInfo, endX: event.clientX, endY: event.clientY }
      setDragInfo((prev) => ({ ...prev, endX: event.clientX, endY: event.clientY }))
      if (onDrag) {
        onDrag(newDragInfo)
      }
    },
    [dragInfo, isDragging, onDrag]
  )

  const handleDragEnd = useCallback(() => {
    setIsDragging(false)
    setDragInfo(null)
    if (onDragEnd) {
      onDragEnd(null)
    }
  }, [onDragEnd])

  useEffect(() => {
    const handler = handlerRef?.current
    if (handler) {
      handler.addEventListener('mousedown', handleDragStart)
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleDragEnd)
    }

    return () => {
      if (handler) {
        handler.removeEventListener('mousedown', handleDragStart)
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleDragEnd)
      }
    }
  }, [handleDragEnd, handleDragStart, handleMouseMove])

  const handlerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

  return (
    <div
      className={classNames(styles['pintxo-drag-handler'], {
        [styles['pintxo-drag-handler--dragging']]: isDragging,
      })}
      ref={handlerRef}
    >
      <svg
        width="49"
        height="8"
        viewBox="0 0 49 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        key="drag-handler"
      >
        <path
          d="M45.5 4.65625C30.5 2.65505 21 2.65699 3 3.65911"
          stroke="#CDCED0"
          strokeWidth="6"
          strokeLinecap="round"
        />
      </svg>
    </div>
  )
}

export default DragHandler
